import { datadogRum } from '@datadog/browser-rum';
import { ddConfig } from './config';

export type AmountBucket = 'lt_50' | '50_500' | '500_5000' | 'gt_5000';

type ActionMap = {
  // Auth
  'auth.login_start':          { auth_method: string };
  'auth.mfa_challenge':        { factor_type: string };
  'auth.login_success':        { auth_method: string; mfa_used: boolean };
  'auth.login_failure':        { failure_reason: string };
  // MFA Account Management
  'mfa.method_enabled':           { factor_type: string };
  'mfa.method_disabled':          { factor_type: string };
  'mfa.recovery_codes_generated': Record<string, never>;
  'mfa.login_verified':           { factor_type: string };
  // Scheduling
  'sched.search':              { modality: string; urgency: string; site_count: number };
  'sched.slot_select':         { modality: string; lead_time_hours: number };
  'sched.confirm':             { modality: string; site_code: string };
  'sched.booked':              { modality: string; appointment_type: string; lead_time_hours: number };
  // Forms
  'form.open':                 { form_id: string; form_version: string };
  'form.section_complete':     { form_id: string; section_id: string };
  'form.submit':               { form_id: string; duration_ms: number; validation_errors_count: number };
  // Documents
  'doc.upload_start':          { doc_type: string; size_kb: number };
  'doc.upload_success':        { doc_type: string; duration_ms: number };
  'doc.view':                  { doc_type: string };
  // Bill Pay
  'pay.start':                 { amount_bucket: AmountBucket };
  'pay.method_select':         { method: string };
  'pay.success':               { amount_bucket: AmountBucket; method: string };
  // Results
  'result.view':               { modality: string; has_images: boolean };
  'result.download':           { modality: string };
  // Referral
  'referral.create':           { modality: string };
  'referral.attachment_added': { doc_type: string };
  'referral.submit':           { modality: string; has_attachments: boolean; duration_ms: number };
};

/**
 * Type-safe wrapper around datadogRum.addAction.
 * Only canonical action names (from ActionMap) are accepted.
 * Use toAmountBucket() and toLeadTimeHours() to produce required attribute values.
 */
export function trackAction<K extends keyof ActionMap>(
  name: K,
  attributes: ActionMap[K],
): void {
  if (!ddConfig.enabled) return;
  try {
    datadogRum.addAction(name, attributes as Record<string, unknown>);
  } catch {
    // Telemetry must never interrupt the caller's flow
  }
}

/** Converts a raw monetary amount to the coarse AmountBucket band. Never pass raw amounts. */
export function toAmountBucket(amount: number): AmountBucket {
  if (amount < 50)   return 'lt_50';
  if (amount < 500)  return '50_500';
  if (amount < 5000) return '500_5000';
  return 'gt_5000';
}

/** Rounds a future slot datetime to the nearest whole lead-time hour. */
export function toLeadTimeHours(slotDateTime: Date): number {
  return Math.round((slotDateTime.getTime() - Date.now()) / 3_600_000);
}
